import { makeStyles, Skeleton, SkeletonItem, Spinner, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  centered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXXL,
  },
  fullPage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100dvh',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  rows: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalM,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 6rem',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
  },
});

interface SpinnerStateProps {
  label?: string;
}

/** A short, in-place wait. Announced politely by Fluent's Spinner. */
export function LoadingState({ label = 'Loading…' }: SpinnerStateProps) {
  const styles = useStyles();

  return (
    <div className={styles.centered}>
      <Spinner label={label} labelPosition="below" />
    </div>
  );
}

/** Whole-screen wait, used while the stored session is being revalidated. */
export function FullPageSpinner({ label = 'Loading…' }: SpinnerStateProps) {
  const styles = useStyles();

  return (
    <div className={styles.fullPage} role="status" aria-live="polite">
      <Spinner label={label} labelPosition="below" size="large" />
    </div>
  );
}

/**
 * Placeholder rows shaped like the documents grid. Matching the final layout
 * means content doesn't jump when the real data lands.
 */
export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  const styles = useStyles();

  return (
    <div className={styles.rows} role="status" aria-live="polite" aria-busy="true">
      <span className="sr-only">Loading documents…</span>
      <Skeleton aria-label="Loading documents">
        {Array.from({ length: rows }, (_unused, index) => (
          <div key={index} className={styles.row}>
            <SkeletonItem size={16} />
            <SkeletonItem size={16} />
            <SkeletonItem size={16} />
            <SkeletonItem size={24} />
          </div>
        ))}
      </Skeleton>
    </div>
  );
}
